import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Home } from "lucide-react";
import { useRouter, journeyStep, type JourneyStep, type Route } from "../../store/router";
import { LANGUAGES, LANGUAGE_LABELS } from "../../i18n/locale";
import { useI18n } from "../../i18n/useI18n";
import { useBranding } from "../../hooks/useBranding";
import { StageErrorBoundary } from "./StageErrorBoundary";
import { crashPolicy } from "../../telemetry/errorMessage";
import { trackVisitEnd } from "../../telemetry/activity";
import { Button } from "../ui/Button";
import { IdleGuard } from "./IdleGuard";
import logoUrl from "../../assets/fiket-logo.svg";

/**
 * HERIT 셸 — 헤더(워드마크 · attract 에서만 언어 토글) + 5단계 진행 표시(원형 스텝 + 연결선) + 스테이지 영역.
 * 헤더의 워드마크를 누르면 첫 화면으로 돌아간다(세션 리셋).
 *
 * 스테이지는 `StageErrorBoundary` 로 감싼다 (BOOTH-97) — 렌더 크래시 횟수는 방문 단위로 세고
 * 방문이 바뀌면 0 으로 돌린다. 같은 방문에서 두 번째 크래시면 `crashPolicy` 가 새로고침을 지시한다.
 */
export function AppShell({ route, children }: { route: Route | null; children: ReactNode }) {
  const step = route ? journeyStep(route) : null;
  const visitId = useRouter((s) => s.visitId);
  const crashes = useRef(0);
  useEffect(() => {
    crashes.current = 0;
  }, [visitId]);
  const onCrash = useCallback(() => {
    crashes.current += 1;
    return crashPolicy(crashes.current);
  }, []);
  return (
    <div className="app">
      <Header route={route} />
      {step ? <Journey step={step} /> : null}
      <div className="stage">
        <StageErrorBoundary onCrash={onCrash}>{children}</StageErrorBoundary>
      </div>
      {/* 전역 무동작 안내 — 완료 전 화면에서 120초 뒤 30초 카운트다운 (2026-10-21) */}
      <IdleGuard route={route} />
    </div>
  );
}

/** 헤더 '처음으로' 를 숨기는 화면 — attract(이미 처음) · thanks(화면 안에 자체 버튼) */
const NO_HOME_ROUTES: readonly Route[] = ["attract", "thanks"];

function Header({ route }: { route: Route | null }) {
  const attract = route === "attract";
  const resetSession = useRouter((s) => s.resetSession);
  const hasShots = useRouter((s) => s.capturedShots.length > 0);
  const { wordmark } = useBranding();
  const { t } = useI18n();
  const [confirming, setConfirming] = useState(false);
  const showHome = route !== null && !NO_HOME_ROUTES.includes(route);
  // 잃을 것이 있으면(촬영 뒤·결제 중) 한 번 확인 — 테마·스타일·동의 단계는 바로 처음으로
  const needsConfirm = hasShots || route === "payment";
  const home = () => {
    setConfirming(false);
    trackVisitEnd("home");
    resetSession();
  };
  const requestHome = () => (needsConfirm ? setConfirming(true) : home());
  // 화면이 바뀌면(무동작 리셋 등) 열린 확인창은 닫는다
  useEffect(() => { setConfirming(false); }, [route]);
  return (
    <header className={attract ? "hd hd--attract" : "hd"}>
      <button type="button" className="brand" onClick={attract ? resetSession : requestHome} aria-label={`${wordmark} — ${t.common.home}`}>
        {/* 로고는 fiket SVG 고정 — 워드마크 문자열은 접근성 라벨(alt·aria-label)로만 쓴다 (2026-10-22) */}
        <img src={logoUrl} alt={wordmark} draggable={false} />
      </button>
      {/* 언어 선택은 첫 화면에서만 — 진행 중에는 워드마크 + '처음으로' (모든 화면에서 돌아갈 길, 2026-10-21) */}
      {attract ? <LanguageToggle /> : null}
      {showHome ? (
        <Button variant="secondary" sm className="hd__home" icon={<Home size={20} strokeWidth={1.8} aria-hidden />} onClick={requestHome}>
          {t.common.home}
        </Button>
      ) : null}
      {confirming ? (
        <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="home-title">
          <div className="dialog__card">
            <h2 id="home-title" className="dialog__title">{t.common.homeConfirmTitle}</h2>
            <p className="dialog__body">{t.common.homeConfirmBody}</p>
            <div className="actions actions--pair dialog__actions">
              <Button variant="secondary" onClick={home}>{t.common.home}</Button>
              <Button onClick={() => setConfirming(false)}>{t.common.keepGoing}</Button>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}

/** 언어 토글 — 한국어 · English · 日本語 · 中文 (BOOTH-29). 순서·라벨은 i18n/locale 의 LANGUAGES/LANGUAGE_LABELS. attract 헤더에서만 렌더. */
function LanguageToggle() {
  const language = useRouter((s) => s.language);
  const setLanguage = useRouter((s) => s.setLanguage);
  return (
    <div className="lang" role="group" aria-label="Language">
      {LANGUAGES.map((l) => (
        <button key={l} type="button" lang={l} aria-pressed={language === l} onClick={() => setLanguage(l)}>
          {LANGUAGE_LABELS[l]}
        </button>
      ))}
    </div>
  );
}

function Journey({ step }: { step: JourneyStep }) {
  const { t } = useI18n();
  // 테마 선택 · 스타일 선택 · 사진 촬영 · 결과 확인 · 사진 받기 (다국어표 스텝퍼 5단계)
  const labels = [t.journey.theme, t.journey.style, t.journey.capture, t.journey.result, t.journey.receive];
  return (
    <nav className="journey" aria-label="progress">
      <ol>
        {labels.map((label, i) => {
          const n = i + 1;
          return (
            <Fragment key={label}>
              {/* 스텝 사이 22px 연결선 — CSS gap 으로는 선을 그릴 수 없어 명시적 요소 */}
              {i > 0 ? <li className="journey__link" aria-hidden /> : null}
              <li aria-current={n === step ? "step" : undefined} className={n < step ? "done" : undefined}>
                <span className="journey__num" aria-hidden>{n}</span>
                <span className="journey__label">{label}</span>
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
